<ng-container *ngIf="!openOffersLoading(); else loadingTemplate">
  <ng-container *ngIf="!openOffersErrorSignal(); else errorMessage">
    <div class="com-table" *ngIf="{ disableConfirmButton: (disableConfirmButton$ | async) } as vm">
      <div class="section">
        <div class="section_container">
          <div class="section-filter">
            <span class="section-filter--show">
              {{ 'COM.CSV.CSV_REG_NAME' | translate }}:
              <h4>{{ displaySelectedReg }}</h4>
            </span>
            <span class="section-filter--show">
              {{ 'COM.CSV.CSV_DATE_RANGE' | translate }}:
              <h4>{{ dateLabel$ | async }}</h4>
            </span>
          </div>

          <div class="section-in-right section-btns">
            <!-- Edit button -->
            <span
              *ngIf="isEditButtonDisplayed()"
              [matTooltip]="'COM.FILTERS.EDIT_DISABLED' | translate"
              [matTooltipDisabled]="applyButtonDisabled()"
              matTooltipPosition="below"
            >
              <button
                [disabled]="!this.applyButtonDisabled()"
                scrollToTop
                mat-stroked-button
                data-cy="open-tab-edit-btn"
                color="primary"
                class="round-button section-btns--edit"
                (click)="triggerEditMode(true)"
              >
                {{ 'COM.BUTTONS.EDIT' | translate }}
              </button>
            </span>

            <!-- More options (export / bulk upload) -->
            <div class="relative-position">
              <button
                *ngIf="selectedTabSignal().exportFunctionality && !editModeSignal()"
                data-cy="open-tab-more-options"
                mat-icon-button
                (click)="toggleDownloadUploadOptions($event)"
                class="more-options-btn"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <div *ngIf="showDownloadUploadOptionsSignal()" class="download-upload-options" (clickOutside)="clickOutsideDownloadUploadOptions()">
                <button mat-menu-item (click)="openExportFunctionalityDialog()" class="download-upload-options--option">
                  <mat-icon class="download-upload-options--option-icon">download</mat-icon>
                  <span>{{ 'COM.BUTTONS.EXPORT_REPORT' | translate }}</span>
                </button>
                <button
                  *ngIf="isBulkImportAllowed()"
                  mat-menu-item
                  (click)="openBulkUploadFunctionalityDialog()"
                  class="download-upload-options--option"
                >
                  <mat-icon class="download-upload-options--option-icon">upload</mat-icon>
                  <span>{{ 'COM.BUTTONS.BULK_UPLOAD' | translate }}</span>
                </button>
              </div>
            </div>

            <!-- Confirm -->
            <button
              *ngIf="editModeSignal()"
              mat-flat-button
              scrollToHeader
              color="primary"
              [disabled]="!vm.disableConfirmButton"
              [ngClass]="{ 'section-btns--confirm--disabled': !vm.disableConfirmButton }"
              data-cy="open-tab-edit-confirm-btn"
              class="round-button section-btns--confirm"
              (click)="save()"
            >
              {{ 'COM.BUTTONS.CONFIRM' | translate }}
            </button>

            <!-- Cancel -->
            <button
              *ngIf="editModeSignal()"
              mat-stroked-button
              scrollToHeader
              color="primary"
              data-cy="open-tab-cancel-btn"
              class="round-button section-btns--cancel"
              (click)="cancel()"
            >
              {{ 'COM.BUTTONS.CANCEL' | translate }}
            </button>
          </div>
        </div>

        <!-- Bulk offers bar (edit mode, non-grouped) -->
        <div class="section_bulk_offers" *ngIf="showBulkOffers()">
          <dr-customer-offers-ui-bulk-offers
            [minDate]="minDate"
            [maxDate]="maxDate"
            [selectedTabOption]="selectedTabSignal()"
            [currentRegViewConfig]="currentRegViewConfigSignal()"
            [isExternalUser]="isExternalUser()"
            [showOfferLock]="true"
          ></dr-customer-offers-ui-bulk-offers>
        </div>
      </div>

      <!-- ================================================================
           TABLE
           ================================================================ -->
      <div class="com-table_container">
        <table mat-table [dataSource]="dataSource" class="mat-elevation-z8" [class.mat-mdc-table--spacing]="!editModeSignal()" multiTemplateDataRows>
          <!-- Offer # column -->
          <ng-container matColumnDef="offer">
            <th mat-header-cell *matHeaderCellDef class="com-table_container--header-offer-row">
              {{ 'COM.LABELS.OFFER_NUM' | translate }}
            </th>
            <td
              mat-cell
              *matCellDef="let element"
              class="com-table_container--offer-column"
              [class.com-table_container--time-column-first-child]="editModeSignal()"
            >
              <b *ngIf="!element?.groupedOffersIntervals">{{ element.index }}</b>
            </td>
          </ng-container>

          <!-- Time Period column -->
          <ng-container matColumnDef="time_period">
            <th mat-header-cell *matHeaderCellDef class="com-table_container--header-row">
              {{ 'COM.LABELS.TIME_FRAME' | translate }}
            </th>
            <td
              mat-cell
              *matCellDef="let element"
              class="com-table_container--time-column"
              [class.com-table_container--time-column-first-child]="editModeSignal()"
            >
              <!-- Standard interval row label -->
              <ng-container *ngIf="!element?.groupedOffersIntervals; else groupedOfferLabel">
                <div class="time-period-labels">
                  <span class="time-period-labels__time">
                    <b>{{ { inputTime: element.timePeriodKey, timeGap: currentRegViewConfigSignal()!.intervalFrequency } | mapTime }}</b>
                    ({{ timezoneAbbr }})
                  </span>
                </div>
              </ng-container>

              <!-- Grouped offer label -->
              <ng-template #groupedOfferLabel>
                <b [innerHTML]="element.timeRangeText"></b>
              </ng-template>
            </td>
          </ng-container>

          <ng-container matColumnDef="offer_name">
            <th mat-header-cell *matHeaderCellDef class="com-table_container--header-row com-table_container--header-offer-name">
              {{ 'COM.LABELS.OFFER_NAME' | translate }}
            </th>
            <td mat-cell *matCellDef="let element" class="com-table_container--offer-name-column">
              <div class="offer-name-labels time-period-labels__time">
                <b>Quantity (kW)</b>
                <b>Price (€)</b>
                <!-- TODO:: We need currency symbol or value from config or endpoints which is missing-->
              </div>
            </td>
          </ng-container>

          <!-- ── Day columns (dynamic) ────────────────────────────────── -->
          <ng-container *ngFor="let column of columns; let i = index" [matColumnDef]="column">
            <th mat-header-cell *matHeaderCellDef class="com-table_container--header-row">
              <ng-container *ngIf="editModeSignal() && !dataSource.data[0]?.groupedOffersIntervals; else headerNonEditMode">
                <mat-checkbox
                  color="primary"
                  class="checkbox-container--readonly checkbox-container--header"
                  (change)="applyBulkOptout(column, $event.checked)"
                  >{{ column | weekDay }} {{ dates[i] | suffixDate }}</mat-checkbox
                >
              </ng-container>
              <ng-template #headerNonEditMode> {{ column | weekDay }} {{ dates[i] | suffixDate }} </ng-template>
            </th>

            <td
              mat-cell
              *matCellDef="let element"
              class="com-table_container--columns"
              [class.com-table_container--columns-last-child]="editModeSignal()"
            >
              <div
                [class.com-table_container--columns-readonly]="!editModeSignal()"
                [class.com-table_container--columns-readonly-last]="i === 6"
                [class.com-table_container--columns-price-edit]="hasPriceEnabled() && editModeSignal()"
              >
                <!-- ===========================================================
                     READONLY MODE
                     =========================================================== -->
                <ng-container *ngIf="!editModeSignal(); else editModeRef">
                  <div [class]="hasPriceEnabled() ? 'td-cell_readonly__with-price' : 'td-cell_readonly'">
                    <!-- Quantity sub-row (readonly) -->
                    <div
                      [class]="hasPriceEnabled() ? 'td-cell_readonly__with-price-container' : 'td-cell_readonly-container'"
                      [class.td-cell_readonly-container--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                    >
                      <span class="td-cell_readonly-body">
                        <span class="flex">
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                            [state]="UIState.GateClosed"
                            name="lock_outlined"
                          ></dr-customer-offers-ui-icons>
                          <!-- [offer_lock] shield — all users, no colour state -->
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].offer_lock"
                            name="shield"
                            fontSet="material-icons-outlined"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons *ngIf="element[columns[i]].opt_out" name="logout_outlined"></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_dst_add_hr || !element[columns[i]].start_date_time"
                            [isSVG]="true"
                            svgName="sunny.svg"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_emergency"
                            [isSVG]="true"
                            svgName="warning.svg"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_holiday"
                            [isSVG]="true"
                            svgName="beach_access.svg"
                          ></dr-customer-offers-ui-icons>
                        </span>
                      </span>
                      <span
                        class="td-cell_readonly-body"
                        [class.td-cell_readonly-body--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                        >{{ element[columns[i]].value }}</span
                      >
                    </div>

                    <!-- Price sub-row (readonly) -->
                    <div
                      *ngIf="hasPriceEnabled()"
                      class="td-cell_readonly__with-price-container"
                      [class.td-cell_readonly-container--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                    >
                      <span class="td-cell_readonly-body">
                        <span class="flex">
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                            [state]="UIState.GateClosed"
                            name="lock_outlined"
                          ></dr-customer-offers-ui-icons>
                          <!-- [offer_lock] -->
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].offer_lock"
                            name="shield"
                            fontSet="material-icons-outlined"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_dst_add_hr || !element[columns[i]].start_date_time"
                            [isSVG]="true"
                            svgName="sunny.svg"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_emergency"
                            [isSVG]="true"
                            svgName="warning.svg"
                          ></dr-customer-offers-ui-icons>
                          <dr-customer-offers-ui-icons
                            *ngIf="element[columns[i]].is_holiday"
                            [isSVG]="true"
                            svgName="beach_access.svg"
                          ></dr-customer-offers-ui-icons>
                        </span>
                      </span>
                      <span
                        class="td-cell_readonly-body"
                        [class.td-cell_readonly-body--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                        >{{ element[columns[i]].price }}</span
                      >
                    </div>
                  </div>
                </ng-container>
                <!-- /READONLY MODE -->

                <!-- EDIT MODE -->
                <ng-template #editModeRef>
                  <!-- Gate-closed cell in edit mode -->
                  <ng-container *ngIf="element[columns[i]].gate_closed && !element[columns[i]].is_emergency; else editValues">
                    <div [class]="hasPriceEnabled() ? 'td-cell--align-with-price' : 'td-cell--align'">
                      <!-- Quantity display (locked) -->
                      <div [class]="hasPriceEnabled() ? 'td-cell_readonly__with-price' : 'td-cell_readonly'">
                        <div
                          [class]="hasPriceEnabled() ? 'td-cell_readonly__with-price-container' : 'td-cell_readonly-container'"
                          [class.td-cell_readonly-container--gate-closed]="element[columns[i]].gate_closed"
                        >
                          <span class="td-cell_readonly-body">
                            <span class="flex">
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].gate_closed"
                                [state]="UIState.GateClosed"
                                name="lock_outlined"
                              ></dr-customer-offers-ui-icons>
                              <!-- [offer_lock] -->
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].offer_lock"
                                name="shield"
                                fontSet="material-icons-outlined"
                              ></dr-customer-offers-ui-icons>
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].is_holiday"
                                [isSVG]="true"
                                svgName="beach_access.svg"
                              ></dr-customer-offers-ui-icons>
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].is_dst_add_hr"
                                [isSVG]="true"
                                svgName="sunny.svg"
                              ></dr-customer-offers-ui-icons>
                            </span>
                          </span>
                          <span
                            class="td-cell_readonly-body"
                            [class.td-cell_readonly-body--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                            >{{ element[columns[i]].value }}</span
                          >
                        </div>
                      </div>

                      <!-- Price display (locked) -->
                      <div *ngIf="hasPriceEnabled()" class="td-cell_readonly__with-price">
                        <div
                          class="td-cell_readonly__with-price-container"
                          [class.td-cell_readonly-container--gate-closed]="element[columns[i]].gate_closed"
                        >
                          <span class="td-cell_readonly-body">
                            <span class="flex">
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].gate_closed"
                                [state]="UIState.GateClosed"
                                name="lock_outlined"
                              ></dr-customer-offers-ui-icons>
                              <!-- [offer_lock] -->
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].offer_lock"
                                name="shield"
                                fontSet="material-icons-outlined"
                              ></dr-customer-offers-ui-icons>
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].is_holiday"
                                [isSVG]="true"
                                svgName="beach_access.svg"
                              ></dr-customer-offers-ui-icons>
                              <dr-customer-offers-ui-icons
                                *ngIf="element[columns[i]].is_dst_add_hr"
                                [isSVG]="true"
                                svgName="sunny.svg"
                              ></dr-customer-offers-ui-icons>
                            </span>
                          </span>
                          <span
                            class="td-cell_readonly-body"
                            [class.td-cell_readonly-body--gate-closed]="element[columns[i]].gate_closed && !element[columns[i]].is_emergency"
                            >{{ element[columns[i]].price }}
                          </span>
                        </div>
                      </div>

                      <!-- Opt-out + Offer-locked (stacked, disabled) -->
                      <div [class]="hasPriceEnabled() ? 'checkbox-container_readonly--with-price' : 'checkbox-container_readonly'">
                        <mat-checkbox
                          color="primary"
                          [class.checkbox-container--mt]="!isExternalUser()"
                          class="checkbox-container--locked-align checkbox-container--readonly"
                          [ngModel]="element[column].opt_out"
                          [disabled]="true"
                          >{{ 'COM.CHECKBOX.OPT_OUT' | translate }}</mat-checkbox
                        >
                        <mat-checkbox
                          *ngIf="!isExternalUser()"
                          color="primary"
                          class="checkbox-container--locked-align checkbox-container--readonly"
                          [ngModel]="element[column].offer_lock"
                          [disabled]="true"
                          >{{ 'COM.LEGEND.OFFER_LOCK' | translate }}</mat-checkbox
                        >
                      </div>
                    </div>
                  </ng-container>
                  <!-- /Gate-closed -->

                  <!-- Editable cell -->
                  <ng-template #editValues>
                    <div [class]="hasPriceEnabled() ? 'td-cell--align-with-price' : 'td-cell--align'">
                      <!-- Quantity input -->
                      <div class="td-cell">
                        <div class="td-cell_custom-input">
                          <div class="td-cell_icons">
                            <!-- [offer_lock] shield beside input — external users only -->
                            <dr-customer-offers-ui-icons
                              *ngIf="isCellLockedForUser(element[columns[i]])"
                              name="shield"
                              fontSet="material-icons-outlined"
                            ></dr-customer-offers-ui-icons>
                            <dr-customer-offers-ui-icons
                              *ngIf="element[columns[i]].is_emergency"
                              [isSVG]="true"
                              svgName="warning.svg"
                            ></dr-customer-offers-ui-icons>
                            <dr-customer-offers-ui-icons
                              *ngIf="element[columns[i]].is_holiday"
                              [isSVG]="true"
                              svgName="beach_access.svg"
                            ></dr-customer-offers-ui-icons>
                            <dr-customer-offers-ui-icons
                              *ngIf="element[columns[i]].is_dst_add_hr || !element[columns[i]].start_date_time"
                              [isSVG]="true"
                              svgName="sunny.svg"
                            ></dr-customer-offers-ui-icons>
                          </div>
                          <input
                            class="td-cell_input"
                            [class.invalid]="element[columns[i]].invalid_value"
                            type="number"
                            [value]="element[columns[i]].value"
                            (blur)="inputToCellValue(element, $event, column)"
                            (wheel)="$event.preventDefault()"
                            validateNumber
                            [max]="currentRegViewConfigSignal()?.maximumValue ?? 0"
                            [min]="currentRegViewConfigSignal()?.minimumValue ?? 0"
                            [untouchedData]="currentOpenOffers()?.values!"
                            [timePeriodKey]="element.timePeriodKey"
                            [week]="column"
                            [disabled]="
                              !element[columns[i]].start_date_time ||
                              !currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit ||
                              currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_opt_out_only ||
                              isCellLockedForUser(element[columns[i]])
                            "
                          />
                        </div>
                      </div>

                      <!-- Price input (only when price is enabled) -->
                      <div *ngIf="hasPriceEnabled()" class="td-cell">
                        <div class="td-cell_custom-input">
                          <!-- [offer_lock] shield beside price input — external users only -->
                          <div *ngIf="isCellLockedForUser(element[columns[i]])" class="td-cell_icons">
                            <dr-customer-offers-ui-icons name="shield" fontSet="material-icons-outlined"></dr-customer-offers-ui-icons>
                          </div>
                          <input
                            class="td-cell_input td-cell_input--price"
                            [class.invalid]="element[columns[i]].invalid_price"
                            type="number"
                            min="0"
                            [value]="element[columns[i]].price ?? null"
                            (blur)="inputToCellPrice(element, $event, column)"
                            (wheel)="$event.preventDefault()"
                            [disabled]="
                              !element[columns[i]].start_date_time ||
                              !currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit ||
                              currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_opt_out_only ||
                              isCellLockedForUser(element[columns[i]])
                            "
                          />
                        </div>
                      </div>

                      <!-- Opt-out + Offer-locked (stacked) -->
                      <div [class]="hasPriceEnabled() ? 'checkbox-container_readonly--with-price' : 'checkbox-container_readonly'">
                        <mat-checkbox
                          color="primary"
                          [class.checkbox-container--mt]="!isExternalUser()"
                          class="checkbox-container--unlocked-align checkbox-container--readonly"
                          [ngModel]="element[column].opt_out"
                          (change)="inputToCellOptOut(element, $event.checked, column)"
                          [disabled]="!element[columns[i]].start_date_time || (isExternalUser() && element[columns[i]].offer_lock)"
                          >{{ 'COM.CHECKBOX.OPT_OUT' | translate }}</mat-checkbox
                        >
                        <mat-checkbox
                          *ngIf="!isExternalUser()"
                          color="primary"
                          class="checkbox-container--unlocked-align checkbox-container--readonly"
                          data-cy="open-tab-offer-locked-checkbox"
                          [ngModel]="element[column].offer_lock"
                          (change)="inputToCellOfferLocked(element, $event.checked, column)"
                          [disabled]="!element[columns[i]].start_date_time"
                          >{{ 'COM.LEGEND.OFFER_LOCK' | translate }}</mat-checkbox
                        >
                      </div>
                    </div>
                  </ng-template>
                  <!-- /Editable cell -->
                </ng-template>
                <!-- /EDIT MODE -->
              </div>
            </td>
          </ng-container>

          <!-- Expand detail row (edit-mode spacing animation) -->
          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
              <div
                class="table-element-detail"
                [class.table-element-detail--padding]="editModeSignal()"
                [class.table-element-detail--padding-internal]="editModeSignal() && !isExternalUser()"
                [@detailExpand]="editModeSignal() ? 'expanded' : 'collapsed'"
              >
                <div class="expand-container"></div>
              </div>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns" class="table-header-row"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns" class="table-element-row" [class.table-expanded-row]="editModeSignal()"></tr>
          <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="table-detail-row"></tr>
        </table>
      </div>

      <!-- Legend footer -->
      <div class="com-table_footer">
        <div class="com-table_footer-icon">
          <dr-customer-offers-ui-icons name="lock_outlined"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.GATE_CLOSED' | translate }}</span>
        </div>
        <!-- [offer_lock] -->
        <div class="com-table_footer-icon">
          <dr-customer-offers-ui-icons name="shield" fontSet="material-icons-outlined"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.OFFER_LOCK' | translate }}</span>
        </div>
        <div *ngIf="!hideForSpain" class="com-table_footer-icon">
          <dr-customer-offers-ui-icons name="logout_outlined"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.OPT_OUT' | translate }}</span>
        </div>
        <div class="com-table_footer-icon">
          <dr-customer-offers-ui-icons [isSVG]="true" svgName="beach_access.svg"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.HOLIDAY' | translate }}</span>
        </div>
        <div class="com-table_footer-icon">
          <dr-customer-offers-ui-icons [isSVG]="true" svgName="warning.svg"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.EDIT_INSIDE_GC' | translate }}</span>
        </div>
        <div class="com-table_footer-icon">
          <dr-customer-offers-ui-icons [isSVG]="true" svgName="sunny.svg"></dr-customer-offers-ui-icons>
          <span>{{ 'COM.LEGEND.DAYLIGHT_SAVINGS' | translate }}</span>
        </div>
      </div>
    </div>
  </ng-container>
</ng-container>

<ng-template #loadingTemplate>
  <div class="loading-page">
    <mat-spinner></mat-spinner>
  </div>
</ng-template>

<ng-template #errorMessage>
  <dr-customer-offers-ui-no-data [title]="errorMessageTitle" [message]="message"></dr-customer-offers-ui-no-data>
</ng-template>
